import routes from '@constants/routes.constant';
import { MenuState } from '@features/main/interfaces/main.interfaces';
import useAuth from '@hooks/auth.hook';
import { MenuRoute } from '@interfaces/auth.interfaces';
import { ArrowBackIosNewRounded, ArrowForwardIosRounded } from '@mui/icons-material';
import clsx from 'clsx';
import { useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Logo from '../Logo';
import MenuPopUp from '../MenuPopUp';
import UserInfo from '../UserInfo';
import { handleListItemClick, handleUserInfoClick } from './Menu.handlers';
import useMenuStyles from './Menu.styles';
import NotificationImage from '@assets/Notification.svg';
import useRequests from '@hooks/requests.hook';
import { useEveryNSeconds } from '@hooks/timer.hook';
import { GlobalContext } from '@utils/RequestContext';

interface Props {
	menuState: MenuState;
	toggleMenu(): void;
}

const Menu: React.FC<Props> = ({ menuState, toggleMenu }) => {
	// State
	const [currentPage, setCurrentPage] = useState<string>(document.location.pathname);
	const [popUpState, setPopUpState] = useState<boolean>(false);
	const [showNotification, setShowNotification] = useState(false);
	const { countRequest, setCountRequest, setUpdateRequestTable } = useContext(GlobalContext);

	// Hooks
	const { getMenu } = useAuth();
	const { requestAdministraCliente } = useRequests();

	const classes = useMenuStyles();
	const navigate = useNavigate();
	const location = useLocation();

	// Listeners
	window.addEventListener('click', () => {
		if (popUpState) {
			setPopUpState(false);
		}
	});

	// Effects
	useEffect(() => {
		if (currentPage !== location.pathname) {
			setCurrentPage(location.pathname);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location.pathname]);

	useEveryNSeconds(() => {
		getData().then((count) => {
			setCountRequest(count);
			setUpdateRequestTable(true);
		});
	});

	useEffect(() => {
		getData();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (+countRequest > 0) {
			setShowNotification(true);
		} else {
			setShowNotification(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [countRequest]);

	const getData = async () => {
		try {
			const response = (await requestAdministraCliente(
				`usuarios/solicitudes/cantidad`,
				'GET'
			)) as any;
			if (response.isError) {
				throw new Error(response.message || response.data.message);
			}
			return Promise.resolve(+response.data.data.cantidad_solicitudes);
		} catch (error: any) {
			return Promise.resolve(+0);
		}
	};

	return (
		<div
			className={clsx(classes.root, {
				[classes.rootBig]: menuState === 'big',
				[classes.rootSmall]: menuState === 'small',
			})}
			id="sidebar-menu"
		>
			<div className={classes.logoContainer} id="sidebar-menu-logo">
				<button
					aria-label={menuState === 'big' ? 'Minimizar' : 'Maximizar'}
					className={clsx(classes.toggleButton, {
						[classes.toggleButtonSmall]: menuState === 'small',
					})}
					id="sidebar-menu-toggle"
					onClick={toggleMenu}
				>
					{menuState === 'big' ? (
						<ArrowBackIosNewRounded sx={{ fontSize: 12 }} />
					) : (
						<ArrowForwardIosRounded sx={{ fontSize: 12 }} />
					)}
				</button>
				<Logo menuState={menuState} />
			</div>
			<div
				className={clsx(classes.menuContainer, {
					[classes.menuContainerSmall]: menuState === 'small',
				})}
				id="sidebar-menu-container"
			>
				<nav>
					{routes.map((route, i) => {
						const menuData: MenuRoute | undefined = route.id
							? getMenu(route.id)
							: undefined;

						if (!menuData || route.hidden) {
							return null;
						}

						return (
							<button
								className={clsx(classes.menuItem, {
									[classes.menuItemActive]: currentPage == route.url,
								})}
								id={route.id}
								key={`route_${route.url}_${i}`}
								onClick={handleListItemClick(route, navigate, setCurrentPage)}
							>
								{route.id === 'gestionar_usuarios_' && showNotification ? (
									<img
										alt={'notification'}
										width={24}
										height={24}
										id={`image`}
										src={NotificationImage}
									/>
								) : (
									<div className={classes.menuItemIcon}>{route.Icon}</div>
								)}

								{menuState === 'big' && (
									<p className={classes.menuItemTitle}>{menuData.name}</p>
								)}
							</button>
						);
					})}
				</nav>
			</div>
			<button
				className={clsx(classes.userInfoContainer, {
					[classes.userInfoContainerSmall]: menuState === 'small',
				})}
				onClick={handleUserInfoClick(popUpState, setPopUpState)}
			>
				<UserInfo
					hasToggle
					id="sidebar-menu_userinfo_content"
					menuState={menuState}
					popUpState={popUpState}
				/>
			</button>
			<MenuPopUp menuState={menuState} open={popUpState} />
		</div>
	);
};

export default Menu;
